import type { LeadRow } from "./api/types";

/**
 * A picture's link less its signature. The API signs every customer's picture
 * afresh on each answer (lazior-api handler.go avatarURL, good for 15
 * minutes), so two answers a second apart name the same picture by two links;
 * this is the part they share.
 */
export function linkKey(url?: string): string {
  if (!url) return "";
  const q = url.indexOf("?");
  return q < 0 ? url : url.slice(0, q);
}

/**
 * How long a copy read earlier is trusted to hold working links: they are
 * signed for 15 minutes, and anything drawn from a copy later than that
 * (a picture's first load) would fail.
 */
export const LINK_FRESH_MS = 10 * 60_000;

/**
 * A customer as compared between answers: everything, with the picture by its
 * link less the signature. Compared whole, every customer with a picture
 * looked changed on every answer: the customer table was redrawn and every
 * picture fetched again whenever one customer changed (2026-10-19), and the
 * chat list and the open chat every 15 seconds (2026-10-20).
 */
export function rowSig(r: { avatar_url?: string }): string {
  return JSON.stringify(r.avatar_url ? { ...r, avatar_url: linkKey(r.avatar_url) } : r);
}

/** Each customer as last answered, by number, with what it was compared by. */
export type KeptRows = Map<string, { sig: string; row: LeadRow }>;

/**
 * A new answer's rows, each one the server sent again unchanged swapped for the
 * object already on screen, so a memo'd row is not drawn again.
 *
 * freshLinks: a kept row takes the newest link to its picture, for an avatar
 * drawn from now on — the customer table and the chat list draw their rows a
 * batch at a time, and a row drawn an hour later must not get a link that ran
 * out. One already drawn keeps the link it loaded (AvatarPic). Without: a row,
 * changed or not, keeps the link its picture was first drawn with.
 */
export function keepRows(before: KeptRows, rows: LeadRow[], freshLinks: boolean): { rows: LeadRow[]; kept: KeptRows } {
  const kept: KeptRows = new Map();
  const next = rows.map((r) => {
    const sig = rowSig(r);
    const had = before.get(r.wa_id);
    let row = r;
    if (had && had.sig === sig) {
      row = had.row;
      if (freshLinks) row.avatar_url = r.avatar_url;
    } else if (had && !freshLinks && r.avatar_url && linkKey(had.row.avatar_url) === linkKey(r.avatar_url)) {
      r.avatar_url = had.row.avatar_url;
    }
    kept.set(r.wa_id, { sig, row });
    return row;
  });
  return { rows: next, kept };
}

/** The same customers in the same order, as the same objects: nothing to redraw. */
export function sameRows(a: LeadRow[] | null, b: LeadRow[]): boolean {
  return !!a && a.length === b.length && a.every((r, i) => r === b[i]);
}
